import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import ErrorBoundary from "./components/ErrorBoundary";
import AppLoading from "./components/AppLoading";

// Lazy-load all pages so each route is its own chunk (critical for mobile LIFF on slow connections)
const Home = lazy(() => import("./pages/Home"));
const Register = lazy(() => import("./pages/Register"));
const Profile = lazy(() => import("./pages/Profile"));
const About = lazy(() => import("./pages/About"));
const PointDetails = lazy(() => import("./pages/PointDetails"));
const PointHistory = lazy(() => import("./pages/PointHistory"));
const RedeemPoints = lazy(() => import("./pages/RedeemPoints"));
const RewardDetail = lazy(() => import("./pages/RewardDetail"));
const RedeemSuccess = lazy(() => import("./pages/RedeemSuccess"));
const MyPrivileges = lazy(() => import("./pages/MyPrivileges"));
const PlaceOrder = lazy(() => import("./pages/PlaceOrder"));
const TaskCreateFirst = lazy(() => import("./pages/TaskCreateFirst"));
const TaskCreateSummary = lazy(() => import("./pages/TaskCreateSummary"));
const OrderForm = lazy(() => import("./pages/OrderForm"));
const OrderFormFull = lazy(() => import("./pages/OrderFormFull"));
const OcrInstruction = lazy(() => import("./pages/OcrInstruction"));
const OrderConfirm = lazy(() => import("./pages/OrderConfirm"));
const OrderSummary = lazy(() => import("./pages/OrderSummary"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const OrderHistory = lazy(() => import("./pages/OrderHistory"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));
const OrderEdit = lazy(() => import("./pages/OrderEdit"));
const ShippingAddress = lazy(() => import("./pages/ShippingAddress"));
// TODO(re-enable address add/edit): restore this import when address management is re-enabled.
// const AddressForm = lazy(() => import("./pages/AddressForm"));
const AddressCreatedWaiting = lazy(() => import("./pages/AddressCreatedWaiting"));
const Faq = lazy(() => import("./pages/Faq"));

function LoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#111]">
      <AppLoading variant="spinner" />
    </div>
  );
}

const ROUTE_TITLES: Record<string, string> = {
  "/": "โพรไฟล์",
  "/liff": "Home",
  "/register": "ลงทะเบียน",
  "/profile": "โพรไฟล์",
  "/address": "ที่อยู่จัดส่ง",
  "/address/new": "เพิ่มที่อยู่",
  "/address/created": "เพิ่มที่อยู่สำเร็จ",
  "/about": "เกี่ยวกับ",
  "/faq": "ติดต่อแอดมิน",
  "/points": "คะแนนสะสม",
  "/points/history": "ประวัติคะแนน",
  "/privileges": "สิทธิพิเศษ",
  "/order": "สั่งซื้อสินค้า",
  "/order/task-create": "สร้าง Task Create",
  "/order/task-create/summary": "สรุป Task Create",
  "/order/form": "กรอกรายละเอียด",
  "/order/form-full": "กรอกรายละเอียด",
  "/order/ocr-instruction": "ฟอร์มทำรายการ",
  "/order/confirm": "ยืนยันคำสั่งซื้อ",
  "/order/summary": "สรุปคำสั่งซื้อ",
  "/order/success": "สั่งซื้อสำเร็จ",
  "/order/history": "ประวัติคำสั่งซื้อ",
  "/redeem": "แลกคะแนน",
  "/redeem/success": "แลกคะแนนสำเร็จ",
};

const APP_NAME = "EssilorLuxottica";

function getTitle(pathname: string): string {
  // Exact match first
  if (ROUTE_TITLES[pathname]) return `${ROUTE_TITLES[pathname]} | ${APP_NAME}`;

  // Dynamic routes: strip last segment and try matching pattern
  const segments = pathname.split("/").filter(Boolean);
  while (segments.length > 1) {
    segments.pop();
    const parent = "/" + segments.join("/");
    if (ROUTE_TITLES[parent]) return `${ROUTE_TITLES[parent]} | ${APP_NAME}`;
  }

  return APP_NAME;
}

function DynamicTitle() {
  const location = useLocation();

  useEffect(() => {
    document.title = getTitle(location.pathname);
  }, [location.pathname]);

  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <Toaster position="top-center" richColors closeButton duration={3000} toastOptions={{ style: { fontFamily: '"Noto Sans Thai", "Noto Sans", sans-serif' } }} />
      <DynamicTitle />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<Profile />} />
          <Route path="/liff" element={<Home />} />
          <Route path="/register" element={<Register />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/address" element={<ShippingAddress />} />
          {/* TODO(re-enable address add/edit): restore the two routes below when address management is re-enabled. */}
          {/* <Route path="/address/new" element={<AddressForm />} /> */}
          {/* <Route path="/address/edit/:id" element={<AddressForm />} /> */}
          <Route path="/address/new" element={<Navigate to="/address" replace />} />
          <Route path="/address/edit/:id" element={<Navigate to="/address" replace />} />
          <Route path="/address/created" element={<AddressCreatedWaiting />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/points" element={<PointDetails />} />
          <Route path="/points/history" element={<PointHistory />} />
          <Route path="/privileges" element={<MyPrivileges />} />
          <Route path="/order" element={<PlaceOrder />} />
          <Route path="/order/task-create" element={<TaskCreateFirst />} />
          <Route path="/order/task-create/summary" element={<TaskCreateSummary />} />
          <Route path="/order/form" element={<OrderForm />} />
          <Route path="/order/form-full" element={<OrderFormFull />} />
          <Route path="/order/ocr-instruction" element={<OcrInstruction />} />
          <Route path="/order/confirm" element={<OrderConfirm />} />
          <Route path="/order/summary" element={<OrderSummary />} />
          <Route path="/order/success" element={<OrderSuccess />} />
          <Route path="/order/history" element={<OrderHistory />} />
          <Route path="/order/history/:id" element={<OrderDetail />} />
          <Route path="/order/edit/:id" element={<OrderEdit />} />
          <Route path="/redeem" element={<RedeemPoints />} />
          <Route path="/redeem/success" element={<RedeemSuccess />} />
          <Route path="/redeem/:id" element={<RewardDetail />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
